import type { AnchorHTMLAttributes, ReactNode } from 'react';
import Markdown, { type Components } from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { resolveLegalSlugFromHref, type LegalSlug } from '@/data/legalDocuments';

interface PolicyMarkdownProps {
  content: string;
  /** Called when the user clicks a link to another policy document within the content. */
  onNavigate: (slug: LegalSlug) => void;
}

function isExternalHref(href: string): boolean {
  return /^([a-z][a-z0-9+.-]*:)?\/\//i.test(href) || href.startsWith('mailto:');
}

function PolicyLink({
  href,
  children,
  onNavigate,
}: AnchorHTMLAttributes<HTMLAnchorElement> & { onNavigate: (slug: LegalSlug) => void }) {
  if (!href) return <span>{children}</span>;

  if (isExternalHref(href)) {
    return (
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        className="text-[#8e4585] underline underline-offset-2 hover:text-[#753a6f] font-medium"
      >
        {children}
      </a>
    );
  }

  const internalSlug = resolveLegalSlugFromHref(href);
  if (internalSlug) {
    return (
      <button
        type="button"
        onClick={() => onNavigate(internalSlug)}
        className="text-[#8e4585] underline underline-offset-2 hover:text-[#753a6f] font-medium"
      >
        {children}
      </button>
    );
  }

  return (
    <a
      href={href}
      className="text-[#8e4585] underline underline-offset-2 hover:text-[#753a6f] font-medium"
    >
      {children}
    </a>
  );
}

function buildComponents(onNavigate: (slug: LegalSlug) => void): Components {
  return {
    h1: ({ children }) => (
      <h1 className="text-xl font-bold text-slate-800 font-['Merriweather'] leading-snug mt-8 mb-3">
        {children}
      </h1>
    ),
    h2: ({ children }) => (
      <h2 className="text-lg font-bold text-slate-800 mt-8 mb-3">{children}</h2>
    ),
    h3: ({ children }) => (
      <h3 className="text-base font-bold text-slate-800 mt-6 mb-2">{children}</h3>
    ),
    h4: ({ children }) => (
      <h4 className="text-sm font-semibold text-slate-700 mt-4 mb-2">{children}</h4>
    ),
    p: ({ children }) => <p className="text-sm text-slate-600 leading-relaxed mb-3">{children}</p>,
    strong: ({ children }) => <strong className="font-bold text-slate-800">{children}</strong>,
    em: ({ children }) => <em className="italic">{children}</em>,
    ul: ({ children }) => (
      <ul className="list-disc list-outside ml-5 mb-3 space-y-1.5">{children}</ul>
    ),
    ol: ({ children }) => (
      <ol className="list-decimal list-outside ml-5 mb-3 space-y-1.5">{children}</ol>
    ),
    li: ({ children }) => <li className="text-sm text-slate-600 leading-relaxed">{children}</li>,
    hr: () => <hr className="my-6 border-slate-200" />,
    blockquote: ({ children }) => (
      <blockquote className="border-l-4 border-[#e5bce5] pl-4 my-3 text-sm text-slate-500 italic">
        {children}
      </blockquote>
    ),
    a: (props) => <PolicyLink {...props} onNavigate={onNavigate} />,
    table: ({ children }) => (
      <div className="overflow-x-auto mb-4 rounded-lg border border-slate-200">
        <table className="w-full text-sm border-collapse">{children}</table>
      </div>
    ),
    thead: ({ children }) => <thead className="bg-slate-50">{children}</thead>,
    tbody: ({ children }) => <tbody className="divide-y divide-slate-100">{children}</tbody>,
    tr: ({ children }) => <tr>{children}</tr>,
    th: ({ children }) => (
      <th className="text-left font-semibold text-slate-700 px-3 py-2 border-b border-slate-200 whitespace-nowrap">
        {children}
      </th>
    ),
    td: ({ children }) => <td className="text-slate-600 px-3 py-2 align-top">{children}</td>,
    code: ({ children }) => (
      <code className="text-[13px] bg-slate-100 text-slate-700 rounded px-1.5 py-0.5">
        {children}
      </code>
    ),
  };
}

export function PolicyMarkdown({ content, onNavigate }: PolicyMarkdownProps): ReactNode {
  return (
    <Markdown remarkPlugins={[remarkGfm]} components={buildComponents(onNavigate)}>
      {content}
    </Markdown>
  );
}
